import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useKid } from "@/context/KidContext";
import { PlayCircle } from "lucide-react";

export default function Series() {
  const { active } = useKid();
  const [series, setSeries] = useState([]);
  const [cont, setCont] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/series");
        setSeries(data.series || []);
        if (active) {
          const { data: cw } = await api.get(`/continue-watching/${active.id}`);
          setCont(cw.items || []);
        }
      } finally { setLoading(false); }
    })();
  }, [active]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-6 sm:pt-8 pb-16">
      <div className="flex items-center gap-3">
        <span className="text-3xl">📺</span>
        <div>
          <h1 className="text-3xl font-extrabold text-[#243B53]">Séries do Clubinho</h1>
          <p className="text-sm text-[#243B53]/70">Aventuras cristãs para descobrir episódio por episódio.</p>
        </div>
      </div>
      <div className="mt-3 h-1.5 rounded-full rainbow-strip opacity-90" />

      {cont.length > 0 && (
        <section className="mt-8" data-testid="continue-watching-block">
          <h2 className="text-lg sm:text-xl font-extrabold text-[#243B53] flex items-center gap-2">▶️ Continuar assistindo</h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {cont.map(({ series: s, next_episode, completed, total }) => (
              <Link key={s.key} to={`/series/${s.key}`} data-testid={`continue-series-${s.key}`}
                className="card-soft p-4 flex gap-4 items-center hover:-translate-y-0.5 transition">
                <div className="w-24 h-16 rounded-2xl overflow-hidden bg-[#FFF9ED] shrink-0">
                  {s.cover_image ? <img src={s.cover_image} alt={s.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-3xl">📺</div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-extrabold text-[#243B53] truncate">{s.title}</div>
                  <div className="text-xs text-[#243B53]/60">Próximo: <b>{next_episode.title}</b></div>
                  <div className="mt-1 progress-track"><div className="progress-fill" style={{ width: `${Math.round(completed/Math.max(1,total)*100)}%` }} /></div>
                  <div className="text-xs text-[#243B53]/60 mt-1">{completed} de {total} concluídos</div>
                </div>
                <PlayCircle className="text-[#D9A441]" size={28} />
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="mt-10">
        <h2 className="text-lg sm:text-xl font-extrabold text-[#243B53]">Todas as séries</h2>
        {loading ? (
          <div className="mt-6 text-[#243B53]/60">Carregando…</div>
        ) : series.length === 0 ? (
          <div className="mt-6 card-soft p-8 text-center text-[#243B53]/60">
            Nenhuma série publicada ainda. Volte em breve! 🌈
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {series.map((s) => (
              <Link key={s.key} to={`/series/${s.key}`} data-testid={`series-card-${s.key}`}
                className="card-soft overflow-hidden hover:-translate-y-1 transition">
                <div className="aspect-[16/9] bg-[#FFF9ED] relative">
                  {s.cover_image ? (
                    <img src={s.cover_image} alt={s.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-6xl">📺</div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 h-1 rainbow-strip opacity-90" />
                </div>
                <div className="p-5">
                  <div className="font-extrabold text-[#243B53] text-lg">{s.title}</div>
                  <div className="text-sm text-[#243B53]/70 line-clamp-2 mt-1">{s.description}</div>
                  <div className="mt-3 flex items-center gap-2 text-xs">
                    <span className="chip chip-sun">{s.episode_count || 0} episódios</span>
                    {s.age_groups?.slice(0,2).map((g) => <span key={g} className="chip chip-leaf">{g} anos</span>)}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
